import { combineReducers, configureStore } from "@reduxjs/toolkit";
import { injectStore } from "apis/utils";
import acronymsReducer from "slices/acronymReport/acronymReportSlice";
import activityLogReducer from "slices/activityLog";
import amendmentsReducer from "slices/amendments/amendmentsSlice";
import appReducer from "slices/app/appSlice";
import errorReducer from "slices/app/errorSlice";
import modalsReducer from "slices/app/modalsSlice";
import { archivedOpportunitiesByReasonReducer } from "slices/archivedOpportunitiesByReason";
import archiveStatusesReducer from "slices/archiveStatuses/archiveStatusesSlice";
import assigneesReducer from "slices/assignees";
import aupAcceptanceReducer from "slices/aupAcceptance/aupAcceptanceSlice";
import bestFitSpreadReducer from "slices/bestFitSpread";
import captureActivityReducer from "slices/captureActivity";
import captureDashboardReducer from "slices/captureDashboard";
import captureNotificationsWidgetReducer from "slices/captureNotificationsWidget";
import cloudUsageReducer from "slices/cloudUsage";
import cloudUsageWorkspaceDetailReducer from "slices/cloudUsageWorkspaceDetail";
import collectionsSlice from "slices/collections/collectionsSlice";
import collectionTypesReducer from "slices/collections/collectionTypesSlice";
import commentsReducer from "slices/comments";
import contractsSlice from "slices/contracts";
import contractTypesReducer from "slices/contracts/contractTypesSlice";
import currencyReducer from "slices/currency";
import dictionariesReducer from "slices/dictionary/dictionarySlice";
import dictionarySearchReducer from "slices/dictionarySearch/dictionarySearchSlice";
import discoveryReducer from "slices/discovery/discoverySlice";
import documentUploadsReducer from "slices/documentUploads/documentUploadsSlice";
import documentViewerReducer from "slices/documentViewer";
import featureFlagsReducer from "slices/featureFlags";
import notificationSettingsReducer from "slices/notificationSettings";
import opportunitySlice from "slices/opportunity";
import { opportunityToProposalReducer } from "slices/opportunityToProposal";
import pipelineByOwnerReducer from "slices/pipelineByOwner";
import pipelineByOwnerRankedReducer from "slices/pipelineByOwnerRanked";
import pipelineFunnelReducer from "slices/pipelineFunnel";
import pipelineValueByCustomerReducer from "slices/pipelineValueByCustomer";
import promptExperimentationReducer from "slices/promptExperimentation";
import promptsReducer from "slices/prompts/promptsSlice";
import proposalOutlineSlice from "slices/proposalOutline/outlineSlice";
import proposalsSlice from "slices/proposals";
import readabilitySlice from "slices/readability/readabilitySlice";
import recentActivityReducer from "slices/recentActivity";
import sessionReducer, { RESET_STORE_ACTION } from "slices/session";
import acronymIgnoreReducer from "slices/settings/slices/acronymIgnoreSlice";
import activeTabReducer from "slices/settings/slices/activeTabSlice";
import addInSettingsReducer from "slices/settings/slices/addInSettingsSlice";
import bestFitProfilesReducer from "slices/settings/slices/bestFitProfilesSlice";
import dictionaryTypesReducer from "slices/settings/slices/dictionaryTypesSlice";
import discoveryIgnoreReducer from "slices/settings/slices/discoveryIgnoreSlice";
import opportunityStagesReducer from "slices/settings/slices/opportunityStagesSlice";
import proposalStagesReducer from "slices/settings/slices/proposalStagesSlice";
import readabilityIgnoreReducer from "slices/settings/slices/readabilityIgnoreSlice";
import thresholdsReducer from "slices/settings/slices/thresholdsSlice";
import shreddingReducer from "slices/shredding";
import systemAdminReducer from "slices/systemAdmin/systemAdminSlice";
import technicalAlignmentReducer from "slices/technicalAlignment/technicalAlignmentSlice";
import timeInStageReducer from "slices/timeInStage";
import topOpportunitiesByBestFitReducer from "slices/topOpportunitiesByBestFit";
import totalPipelineReducer from "slices/totalPipeline";
import usageStatsReducer from "slices/usageStats";
import userAdminReducer from "slices/userAdmin/userAdminSlice";
import visiAttachedDocumentsReducer from "slices/visi/attachedDocumentsSlice";
import visiCitationsReducer from "slices/visi/citationsSlice";
import reindexProgressReducer from "slices/visi/reindexProgressSlice";
import watchwordsSlice from "slices/watchwords";
import chooseFilesModalReducer from "slices/workspace/chooseFilesModalSlice";
import foldersReducer from "slices/workspace/foldersSlice";
import workspaceReducer from "slices/workspace/workspaceSlice";
import workspaceV2Reducer from "slices/workspace/workspaceV2Slice";
import writingSlice from "slices/writing";
import { isAddInWebView } from "utils/add-in";
import { sentryReduxEnhancer } from "utils/sentry";

import addInReducer from "./addIn";
import { adminReducer } from "./admin";
import dashboardReducer from "./dashboard/dashboardSlice";
import generativeAiReducer from "./generativeAi/generativeAiSlice";
import miscAnalysisReducer from "./miscAnalysis/miscAnalysisSlice";
import notificationsReducer from "./notifications/notificationsSlice";
import allowedSitesReducer from "./sharepoint/allowedSitesSlice";
import sharepointReducer from "./sharepoint/sharepointSlice";
import tableReducer from "./table/tableSlice";
import { actionLogger } from "./utils";

const IS_DEVELOPMENT = process.env.NODE_ENV === "development";
const FORCE_LOG_ACTIONS =
  IS_DEVELOPMENT && process.env.REACT_APP_CONSOLE_LOG_ACTIONS === "true";

export const setupStore = (preloadedState) => {
  const combinedReducer = combineReducers({
    addIn: addInReducer,
    app: appReducer,
    amendments: amendmentsReducer,
    admin: adminReducer,
    dashboard: dashboardReducer,
    generativeAi: generativeAiReducer,
    modals: modalsReducer,
    assignees: assigneesReducer,
    error: errorReducer,
    userAdmin: userAdminReducer,
    systemAdmin: systemAdminReducer,
    dictionaries: dictionariesReducer,
    discovery: discoveryReducer,
    dictionarySearch: dictionarySearchReducer,
    documentUploads: documentUploadsReducer,
    documentViewer: documentViewerReducer,
    currency: currencyReducer,
    featureFlags: featureFlagsReducer,
    notificationSettings: notificationSettingsReducer,
    folders: foldersReducer,
    workspace: workspaceReducer,
    workspaceV2: workspaceV2Reducer,
    opportunity: opportunitySlice,
    proposals: proposalsSlice,
    contracts: contractsSlice,
    prompts: promptsReducer,
    promptExperimentation: promptExperimentationReducer,
    proposalOutline: proposalOutlineSlice.reducer,
    chooseFilesModal: chooseFilesModalReducer,
    readability: readabilitySlice,
    visi: combineReducers({
      attachedDocuments: visiAttachedDocumentsReducer,
      citations: visiCitationsReducer,
      reindexProgress: reindexProgressReducer,
    }),
    settings: combineReducers({
      activeTab: activeTabReducer,
      acronymIgnore: acronymIgnoreReducer,
      addInSettings: addInSettingsReducer,
      dictionaryTypes: dictionaryTypesReducer,
      discoveryIgnore: discoveryIgnoreReducer,
      opportunityStages: opportunityStagesReducer,
      proposalStages: proposalStagesReducer,
      readabilityIgnore: readabilityIgnoreReducer,
      thresholds: thresholdsReducer,
      bestFitProfiles: bestFitProfilesReducer,
      archiveStatuses: archiveStatusesReducer,
    }),
    sharepoint: sharepointReducer,
    sharepointAllowedSites: allowedSitesReducer,
    shredding: shreddingReducer,
    writing: writingSlice,
    table: tableReducer,
    cloudUsage: cloudUsageReducer,
    cloudUsageWorkspaceDetail: cloudUsageWorkspaceDetailReducer,
    collections: collectionsSlice,
    collectionTypes: collectionTypesReducer,
    comments: commentsReducer,
    contractTypes: contractTypesReducer,
    miscAnalysis: miscAnalysisReducer,
    notifications: notificationsReducer,
    technicalAlignment: technicalAlignmentReducer,
    usageStats: usageStatsReducer,
    activityLog: activityLogReducer,
    capture: combineReducers({
      activity: captureActivityReducer,
      dashboard: captureDashboardReducer,
      pipelineByOwner: pipelineByOwnerReducer,
      pipelineByOwnerRanked: pipelineByOwnerRankedReducer,
      pipelineFunnel: pipelineFunnelReducer,
      pipelineValueByCustomer: pipelineValueByCustomerReducer,
      bestFitSpread: bestFitSpreadReducer,
      captureNotificationsWidget: captureNotificationsWidgetReducer,
      recentActivity: recentActivityReducer,
      timeInStage: timeInStageReducer,
      topOpportunitiesByBestFit: topOpportunitiesByBestFitReducer,
      totalPipeline: totalPipelineReducer,
      opportunityToProposal: opportunityToProposalReducer,
      archivedOpportunitiesByReason: archivedOpportunitiesByReasonReducer,
    }),
    acronyms: acronymsReducer,
    watchwords: watchwordsSlice,
    session: sessionReducer,
    aupAcceptance: aupAcceptanceReducer,
  });

  const rootReducer = (state, action) => {
    if (action.type === RESET_STORE_ACTION) {
      return combinedReducer(undefined, action);
    }
    return combinedReducer(state, action);
  };

  return configureStore({
    reducer: rootReducer,
    enhancers: (getDefaultEnhancers) => {
      return getDefaultEnhancers().concat(sentryReduxEnhancer);
    },
    // buttonAction is a function, and generally we shouldn't
    // store "non-serializable" values in the store. So just tell
    // redux to ignore it as this surpresses the spam of errors.
    middleware: (getDefaultMiddleware) => {
      const middleware = getDefaultMiddleware({
        serializableCheck: {
          ignoredActionPaths: [
            "meta.arg.nodes",
            "payload.buttonAction",
            "payload.modalProps",
          ],
          ignoredActions: ["folders/setEventSource"],
          ignoredPaths: [
            "app.actionRequired.buttonAction",
            "modals",
            "payload.modalProps.onClick",
            "folders.eventSource",
          ],
        },
      });
      return FORCE_LOG_ACTIONS ? middleware.concat(actionLogger) : middleware;
    },
    preloadedState,
  });
};

const store = setupStore();

// This prevents a cicular dependency caused by apis/localhost
// accessing the store ... when the store has actions that access apis/localhost.
injectStore(store);

if (IS_DEVELOPMENT && isAddInWebView()) {
  window.__store = store;
}

export { store };
